// financeCalculators.js - Financial Calculator Utilities

// Regular SIP Calculator (Monthly SIP)
export function calculateSIP({ sip, finalAmount, durationYears, rate = 12 }) {
  const r = rate / 100 / 12; // monthly rate
  const n = durationYears * 12; // total months

  if (sip) {
    const futureValue = sip * (((1 + r) ** n - 1) * (1 + r)) / r;
    return { futureValue: parseFloat(futureValue.toFixed(2)) };
  } else if (finalAmount) {
    const sipRequired = finalAmount * r / (((1 + r) ** n - 1) * (1 + r));
    return { sipRequired: parseFloat(sipRequired.toFixed(2)) };
  } else {
    return { error: "Provide either SIP or Final Amount" };
  }
}

// Step-up SIP Calculator (Annual Step-up)
export function calculateStepUpSIP({ initialSIP, finalAmount, durationYears, rate = 12, stepUpPercent = 10 }) {
  const r = rate / 100 / 12; // monthly rate
  const n = durationYears * 12; // total months
  const stepUp = stepUpPercent / 100;

  if (initialSIP) {
    let total = 0;
    for (let i = 0; i < n; i++) {
      const yearIndex = Math.floor(i / 12); // step-up annually
      const sip_i = initialSIP * ((1 + stepUp) ** yearIndex);
      total += sip_i * ((1 + r) ** (n - i));
    }
    return { futureValue: parseFloat(total.toFixed(2)) };
  } else {
    return { error: "Reverse calculation for Step-up SIP is not supported yet" };
  }
}

// SWP Calculator (Monthly Withdrawal)
export function calculateSWP({ swp, corpus, durationYears, rate = 12 }) {
  const r = rate / 100 / 12; // monthly rate
  const n = durationYears * 12; // total months

  if (swp) {
    const corpusRequired = swp * ((1 - (1 + r) ** -n) / r);
    return { corpusRequired: parseFloat(corpusRequired.toFixed(2)) };
  } else if (corpus) {
    const swpAmount = corpus * r / (1 - (1 + r) ** -n);
    return { swpAmount: parseFloat(swpAmount.toFixed(2)) };
  } else {
    return { error: "Provide either SWP or Corpus" };
  }
}

// Future Value Calculator
export function calculateFutureValue({ initialInvestment = 0, annualContribution = 0, returnRate = 12, durationYears = 10 }) {
  const r = returnRate / 100; // annual rate as decimal
  const n = durationYears; // number of years

  // Future value of lump sum
  const futureValueLumpSum = initialInvestment * (1 + r) ** n;

  // Future value of annuity (annual contributions)
  const futureValueAnnuity = annualContribution > 0 ?
    annualContribution * (((1 + r) ** n - 1) / r) : 0;

  const totalFutureValue = futureValueLumpSum + futureValueAnnuity;

  return {
    futureValue: parseFloat(totalFutureValue.toFixed(2)),
    lumpSumGrowth: parseFloat(futureValueLumpSum.toFixed(2)),
    annuityGrowth: parseFloat(futureValueAnnuity.toFixed(2))
  };
}

// Tier 1 city appreciation data
const appreciationRates = {
  "Mumbai": { "2025": 5.9, "2026": 6.2, "2027": 6.5, "2028": 6.7, "2029": 6.9, "2030": 7.0, "2031": 7.2, "2032": 7.3, "2033": 7.5, "2034": 7.6 },
  "Delhi NCR": { "2025": 2.9, "2026": 4.5, "2027": 5.0, "2028": 5.5, "2029": 6.0, "2030": 6.2, "2031": 6.5, "2032": 6.7, "2033": 6.9, "2034": 7.0 },
  "Bangalore": { "2025": 13.1, "2026": 9.0, "2027": 8.5, "2028": 8.0, "2029": 7.5, "2030": 7.2, "2031": 7.0, "2032": 6.8, "2033": 6.5, "2034": 6.2 },
  "Hyderabad": { "2025": 4.8, "2026": 5.5, "2027": 6.0, "2028": 6.2, "2029": 6.5, "2030": 6.7, "2031": 6.9, "2032": 7.0, "2033": 7.2, "2034": 7.5 },
  "Pune": { "2025": 6.8, "2026": 7.0, "2027": 7.2, "2028": 7.4, "2029": 7.5, "2030": 7.6, "2031": 7.7, "2032": 7.8, "2033": 8.0, "2034": 8.2 },
  "Chennai": { "2025": 9.0, "2026": 8.5, "2027": 8.0, "2028": 7.5, "2029": 7.2, "2030": 7.0, "2031": 6.8, "2032": 6.6, "2033": 6.5, "2034": 6.3 },
  "Kolkata": { "2025": 9.6, "2026": 8.0, "2027": 7.5, "2028": 7.0, "2029": 6.5, "2030": 6.2, "2031": 6.0, "2032": 5.8, "2033": 5.5, "2034": 5.2 },
  "Ahmedabad": { "2025": 6.1, "2026": 6.3, "2027": 6.5, "2028": 6.7, "2029": 6.9, "2030": 7.0, "2031": 7.2, "2032": 7.3, "2033": 7.5, "2034": 7.6 }
};

function getAppreciationRates(city, startYear, durationYears) {
  const cityRates = appreciationRates[city];
  const rates = [];
  for (let i = 0; i < durationYears; i++) {
    const year = (startYear + i).toString();
    rates.push(cityRates?.[year] || 5.0); // default fallback
  }
  return rates;
}

// Rent vs Buy Calculator
export function calculateRentVsBuy({
  city,
  monthlyRent,
  annualRentIncreasePercent,
  homePrice,
  downPaymentPercent,
  loanInterestRate,
  loanTenureYears,
  investmentReturnRate,
  startYear = 2025
}) {
  const months = loanTenureYears * 12;
  const downPayment = homePrice * (downPaymentPercent / 100);
  const loanAmount = homePrice - downPayment;
  const monthlyInterestRate = loanInterestRate / 100 / 12;

  const emi = loanAmount * monthlyInterestRate * Math.pow(1 + monthlyInterestRate, months) /
    (Math.pow(1 + monthlyInterestRate, months) - 1);

  let homeValue = homePrice;
  const appreciation = getAppreciationRates(city, startYear, loanTenureYears);
  for (let rate of appreciation) {
    homeValue *= (1 + rate / 100);
  }

  let investedAmount = downPayment;
  let currentRent = monthlyRent;
  const yearlyData = [];

  for (let i = 0; i < months; i++) {
    const monthlyReturn = investmentReturnRate / 100 / 12;
    const monthlySavings = Math.max(emi - currentRent, 0);
    investedAmount = (investedAmount + monthlySavings) * (1 + monthlyReturn);

    if ((i + 1) % 12 === 0) {
      const year = Math.floor(i / 12) + 1;
      yearlyData.push({
        year,
        investmentValue: parseFloat(investedAmount.toFixed(2)),
        homeValue: parseFloat((homePrice * Math.pow(1 + appreciation[year - 1] / 100, year)).toFixed(2)),
        totalRentPaid: parseFloat((currentRent * 12 * year).toFixed(2))
      });
      currentRent *= (1 + annualRentIncreasePercent / 100);
    }
  }

  return {
    emi: parseFloat(emi.toFixed(2)),
    totalInvestedValue: parseFloat(investedAmount.toFixed(2)),
    finalHomeValue: parseFloat(homeValue.toFixed(2)),
    downPayment: parseFloat(downPayment.toFixed(2)),
    yearlyData,
    recommendation: investedAmount > homeValue ? 'Renting may be better' : 'Buying may be better'
  };
}

// Generate SWP projection data for charts
export function generateSWPProjection({ corpus, swp, durationYears, rate = 12 }) {
  const r = rate / 100 / 12; // monthly rate
  const months = durationYears * 12;
  let remainingCorpus = corpus;
  const projectionData = [];

  for (let i = 0; i <= months; i++) {
    if (i > 0) {
      remainingCorpus = remainingCorpus * (1 + r) - swp;
      if (remainingCorpus < 0) remainingCorpus = 0;
    }

    if (i % 12 === 0) {
      projectionData.push({
        year: i / 12,
        remainingCorpus: parseFloat(remainingCorpus.toFixed(2)),
        totalWithdrawn: parseFloat((swp * i).toFixed(2))
      });
    }
  }

  return projectionData;
}

// -----------------------------------------------------------------------------
// Retirement Calculator – deterministic + optional Monte Carlo (enhanced)
// -----------------------------------------------------------------------------

// --- Shared helpers ----------------------------------------------------------
const toDate = (d) => (d instanceof Date ? d : new Date(d));
const normRate = (r) => (r > 1 ? r / 100 : r); // accepts 10 or 0.10
const isFiniteNumber = (x) => typeof x === "number" && Number.isFinite(x);
const round2 = (x) => Math.round((x + Number.EPSILON) * 100) / 100;

// Actual/365 helpers
const yearsBetweenA365 = (d1, d2) => {
  const ms = toDate(d2).getTime() - toDate(d1).getTime();
  return ms / (1000 * 60 * 60 * 24 * 365);
};

const addYearsA365 = (d, years) => {
  const ms = toDate(d).getTime() + years * 365 * 24 * 3600 * 1000;
  return new Date(ms);
};

// --- Monte Carlo helpers -----------------------------------------------------

function randomStandardNormal() {
  let u = 0;
  let v = 0;
  while (u === 0) u = Math.random();
  while (v === 0) v = Math.random();

  return Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
}

function percentile(sortedArr, p) {
  if (!sortedArr || sortedArr.length === 0) return 0;
  const idx = (sortedArr.length - 1) * p;
  const lower = Math.floor(idx);
  const upper = Math.ceil(idx);

  if (lower === upper) return sortedArr[lower];

  const weight = idx - lower;
  return sortedArr[lower] * (1 - weight) + sortedArr[upper] * weight;
}

// Deterministic retirement projection
export function generateRetirementProjection({
  corpus,
  firstYearExpense,
  yearsInRetirement,
  inflation,
  rate,
  withdrawalTiming = 'end',
}) {
  const projection = [];
  let remainingCorpus = Number(corpus || 0);
  let annualExpense = Number(firstYearExpense || 0);

  projection.push({ year: 0, remainingCorpus: round2(Math.max(0, remainingCorpus)), annualExpense: 0 });

  for (let year = 1; year <= yearsInRetirement; year++) {
    let annualReturn = rate;
    if (annualReturn <= -1) annualReturn = -0.99;

    if (withdrawalTiming === 'start') {
      remainingCorpus -= annualExpense;
      if (remainingCorpus < 0) remainingCorpus = 0;
      remainingCorpus *= (1 + annualReturn);
    } else {
      remainingCorpus *= (1 + annualReturn);
      remainingCorpus -= annualExpense;
      if (remainingCorpus < 0) remainingCorpus = 0;
    }

    projection.push({ year, remainingCorpus: round2(remainingCorpus), annualExpense: round2(annualExpense) });
    annualExpense *= (1 + inflation);
  }

  return projection;
}

// Monte Carlo simulation for retirement corpus sustainability
export function runRetirementMonteCarlo({
  startingCorpus,
  firstYearExpense,
  yearsInRetirement,
  inflation,
  meanReturn,
  volatility,
  simulations = 1000,
  withdrawalTiming = 'end',
}) {
  const yearlyBuckets = Array.from({ length: yearsInRetirement + 1 }, () => []);
  const endingCorpusValues = [];
  let successCount = 0;

  for (let sim = 0; sim < simulations; sim++) {
    let corpus = startingCorpus;
    let annualExpense = firstYearExpense;
    let ruined = false;

    yearlyBuckets[0].push(round2(Math.max(0, corpus)));

    for (let year = 1; year <= yearsInRetirement; year++) {
      let annualReturn = meanReturn + volatility * randomStandardNormal();
      if (annualReturn <= -1) annualReturn = -0.99;

      if (withdrawalTiming === 'start') {
        corpus -= annualExpense;
        if (corpus <= 0) {
          corpus = 0;
          yearlyBuckets[year].push(0);
          for (let y = year + 1; y <= yearsInRetirement; y++) yearlyBuckets[y].push(0);
          ruined = true;
          break;
        }
        corpus *= (1 + annualReturn);
      } else {
        corpus *= (1 + annualReturn);
        corpus -= annualExpense;
        if (corpus <= 0) {
          corpus = 0;
          yearlyBuckets[year].push(0);
          for (let y = year + 1; y <= yearsInRetirement; y++) yearlyBuckets[y].push(0);
          ruined = true;
          break;
        }
      }

      yearlyBuckets[year].push(round2(Math.max(0, corpus)));
      annualExpense *= (1 + inflation);
    }

    if (!ruined && corpus > 0) successCount++;
    endingCorpusValues.push(round2(Math.max(0, corpus)));
  }

  const yearlyPercentileBands = yearlyBuckets.map((arr, year) => {
    const sorted = [...arr].sort((a, b) => a - b);
    return {
      year,
      p10: round2(percentile(sorted, 0.10)),
      p25: round2(percentile(sorted, 0.25)),
      p50: round2(percentile(sorted, 0.50)),
      p75: round2(percentile(sorted, 0.75)),
      p90: round2(percentile(sorted, 0.90)),
    };
  });

  const sortedEnding = [...endingCorpusValues].sort((a, b) => a - b);

  return {
    simulations,
    probabilityOfSuccess: round2((successCount / simulations) * 100),
    probabilityOfRuin: round2(((simulations - successCount) / simulations) * 100),
    // convenience fields for UI
    survivalProbability: round2((successCount / simulations) * 100),
    survivedCount: successCount,
    leftoverCorpusPercentiles: {
      p10: round2(percentile(sortedEnding, 0.10)),
      p25: round2(percentile(sortedEnding, 0.25)),
      p50: round2(percentile(sortedEnding, 0.50)),
      p75: round2(percentile(sortedEnding, 0.75)),
      p90: round2(percentile(sortedEnding, 0.90)),
    },
    yearlyPercentileBands,
  };
}

// Enhanced retirement calculator – deterministic + optional Monte Carlo
export function calculateRetirementFundingGap(input) {
  // --- Read & normalize inputs ----------------------------------------------
  const dob = toDate(input.dateOfBirth);
  const currentDate = input.currentDate ? toDate(input.currentDate) : new Date();

  const retirementAge = Number(input.retirementAgeYears);
  const lifeExpectancy = Number(input.lifeExpectancyYears);
  const inflation = normRate(Number(input.inflation));
  const currentMonthlyExpense = Number(input.currentMonthlyExpense);
  const retWork = normRate(Number(input.expectedReturnWorking));
  const retRet = normRate(Number(input.expectedReturnRetired));
  const reductionPct = normRate(Number(input.expenseReductionPct));
  const currentSavings = Number(input.currentSavings || 0);
  const retirementBenefitsLumpSum = Number(input.retirementBenefitsLumpSum || 0);

  // Monte Carlo inputs
  const enableMonteCarlo = !!input.enableMonteCarlo;
  const retirementVolatility = normRate(Number(input.retirementVolatility || 0));
  const simulations = Number(input.simulations || 1000);
  const monteCarloStartingCorpusMode = input.monteCarloStartingCorpusMode || 'requiredCorpus';

  // Options
  const options = {
    retirementDateFromYears: input?.options?.retirementDateFromYears ?? true,
    inflationYearsOverride: input?.options?.inflationYearsOverride ?? null,
    withdrawalTiming: input?.options?.withdrawalTiming ?? input.withdrawalTiming ?? 'end',
  };

  // --- Validations -----------------------------------------------------------
  if (!(dob instanceof Date) || Number.isNaN(dob.getTime())) return { error: 'Invalid dateOfBirth' };
  if (retirementAge < 35 || retirementAge > 80) return { error: 'retirementAgeYears must be in [35, 80]' };
  if (lifeExpectancy < retirementAge || lifeExpectancy > 110) return { error: 'lifeExpectancyYears must be >= retirementAgeYears and <= 110' };
  if (!isFiniteNumber(inflation) || inflation < 0) return { error: 'Invalid inflation' };
  if (!isFiniteNumber(retWork) || retWork < 0) return { error: 'Invalid expectedReturnWorking' };
  if (!isFiniteNumber(retRet)) return { error: 'Invalid expectedReturnRetired' };
  if (!isFiniteNumber(retirementVolatility) || retirementVolatility < 0) return { error: 'retirementVolatility must be >= 0' };
  if (retirementVolatility > 1) return { error: 'retirementVolatility looks too high. Use 12 for 12% or 0.12.' };
  if (simulations < 100 || simulations > 20000) return { error: 'simulations must be between 100 and 20000' };
  if (!isFiniteNumber(reductionPct) || reductionPct < 0 || reductionPct >= 1) return { error: 'expenseReductionPct must be in [0, 1)' };
  if (!isFiniteNumber(currentMonthlyExpense) || currentMonthlyExpense < 0) return { error: 'currentMonthlyExpense must be >= 0' };

  // --- Core deterministic calculations --------------------------------------
  const currentAgeYears = yearsBetweenA365(dob, currentDate);
  const yearsToRetirement = retirementAge - currentAgeYears;

  const retirementDate = options.retirementDateFromYears
    ? addYearsA365(currentDate, yearsToRetirement)
    : new Date(dob.getFullYear() + retirementAge, dob.getMonth(), dob.getDate());

  const currentAnnualExpense = currentMonthlyExpense * 12;

  const inflationYears = options.inflationYearsOverride ?? yearsToRetirement;
  const monthlyAtRetirement = currentMonthlyExpense * Math.pow(1 + inflation, Math.max(0, inflationYears));
  const monthlyAfterReduction = monthlyAtRetirement * (1 - reductionPct);
  const annualAtRetirement = monthlyAfterReduction * 12;

  const yearsInRetirement = Math.max(0, lifeExpectancy - retirementAge);

  // Required corpus (PV of growing annuity)
  const i = retRet;
  const g = inflation;
  let pvNeededAtRetirement = 0;

  if (yearsInRetirement === 0) {
    pvNeededAtRetirement = 0;
  } else if (Math.abs(i - g) < 1e-12) {
    pvNeededAtRetirement = annualAtRetirement * yearsInRetirement;
  } else {
    const ratio = Math.pow((1 + g) / (1 + i), yearsInRetirement);
    pvNeededAtRetirement = annualAtRetirement * (1 - ratio) / (i - g);
  }

  const fvSavingsAtRetirement = currentSavings * Math.pow(1 + retWork, Math.max(0, yearsToRetirement));
  const totalFundsAtRetirement = retirementBenefitsLumpSum + fvSavingsAtRetirement;
  const fundingGapAtRetirement = pvNeededAtRetirement - totalFundsAtRetirement;

  // Deterministic projection for chart
  const deterministicRetirementProjection = generateRetirementProjection({
    corpus: pvNeededAtRetirement,
    firstYearExpense: annualAtRetirement,
    yearsInRetirement,
    inflation,
    rate: retRet,
    withdrawalTiming: options.withdrawalTiming,
  });

  // Optional Monte Carlo
  let monteCarlo = null;
  let assumptionsForSimulation = null;

  if (enableMonteCarlo && yearsInRetirement > 0) {
    const simulationCorpus = monteCarloStartingCorpusMode === 'availableFunds' ? totalFundsAtRetirement : pvNeededAtRetirement;

    monteCarlo = runRetirementMonteCarlo({
      startingCorpus: simulationCorpus,
      firstYearExpense: annualAtRetirement,
      yearsInRetirement,
      inflation,
      meanReturn: retRet,
      volatility: retirementVolatility,
      simulations,
      withdrawalTiming: options.withdrawalTiming,
    });

    assumptionsForSimulation = {
      startingCorpusMode: monteCarloStartingCorpusMode,
      simulatedStartingCorpus: round2(simulationCorpus),
      firstYearRetirementExpense: round2(annualAtRetirement),
      retirementVolatility,
      simulations,
      withdrawalTiming: options.withdrawalTiming,
    };
  }

  return {
    // Inputs echoed back
    inputs: {
      dateOfBirth: dob.toISOString().slice(0, 10),
      currentDate: currentDate.toISOString(),
      retirementAgeYears: retirementAge,
      lifeExpectancyYears: lifeExpectancy,
      inflation,
      expectedReturnWorking: retWork,
      expectedReturnRetired: retRet,
      expenseReductionPct: reductionPct,
      currentMonthlyExpense,
      currentSavings,
      retirementBenefitsLumpSum,
      enableMonteCarlo,
      retirementVolatility,
      simulations,
      monteCarloStartingCorpusMode,
    },

    // Existing outputs (kept unchanged)
    currentAgeYears: round2(currentAgeYears),
    currentAnnualExpense: round2(currentAnnualExpense),
    retirementDate: retirementDate.toISOString().slice(0, 10),
    yearsToRetirement: round2(yearsToRetirement),
    monthlyExpensesAtRetirement: round2(monthlyAtRetirement),
    monthlyExpensesAfterReduction: round2(monthlyAfterReduction),
    annualExpensesAtRetirement: round2(annualAtRetirement),
    yearsInRetirement,
    pvOfAnnuityNeededAtRetirement: round2(pvNeededAtRetirement),
    fvSavingsAtRetirement: round2(fvSavingsAtRetirement),
    totalFundsAtRetirement: round2(totalFundsAtRetirement),
    fundingGapAtRetirement: round2(fundingGapAtRetirement),
    hasSurplus: fundingGapAtRetirement < 0,

    // New fields
    deterministicRetirementProjection,
    monteCarlo,
    assumptionsForSimulation,

    // Reconciliation
    optionsUsed: {
      retirementDateFromYears: !!options.retirementDateFromYears,
      inflationYearsUsed: Number(inflationYears.toFixed(6)),
      withdrawalTiming: options.withdrawalTiming,
    },
  };
}
